import { store } from './state.js';
import { pickWeighted, generatePhone, priceFor, buildExample, mulberry32 } from './store.js';
import { FIRST_NAMES } from './data.js';
import { APPLIANCE_DIRECTIONS, DIRECTIONS_BY_ID } from './directionsData.js';
import { formatMoney } from './utils.js';
import { showToast } from './toast.js';

const rng = mulberry32(Date.now() % 1000000 + 7);
let hasInteracted = false;
let timer = null;
let wasBelowThreshold = false;
let wasLimitReached = false;

['click', 'keydown', 'touchstart'].forEach(evt => {
    document.addEventListener(evt, () => { hasInteracted = true; }, { once: true, passive: true });
});

function playSound() {
    if (!hasInteracted) return;
    if (!store.get().settings.channels.sound) return;
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [660, 880].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = freq;
            osc.connect(gain);
            gain.connect(ctx.destination);
            gain.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.12);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.25);
            osc.start(ctx.currentTime + i * 0.12);
            osc.stop(ctx.currentTime + i * 0.12 + 0.25);
        });
    } catch (e) { /* audio unavailable */ }
}

function maybeSystemNotification(title, body) {
    if (!store.get().settings.channels.browser) return;
    if (document.visibilityState !== 'hidden') return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
        new Notification(title, { body });
    } catch (e) { /* ignore */ }
}

function generateLead() {
    const state = store.get();
    const activeDirectionIds = store.activeDirectionIds().filter(id => DIRECTIONS_BY_ID[id]);
    const activeCities = store.activeCities();
    if (!activeDirectionIds.length || !activeCities.length) return null;

    const city = pickWeighted(rng, activeCities, c => c.weight);
    const cityState = store.getCityState(city.id);
    const canUseRegion = cityState.regionOn && city.regionTowns.length;
    const useRegion = canUseRegion && (!cityState.cityOn || rng() < 0.22);

    let place;
    if (useRegion) {
        place = { label: `${city.regionTowns[Math.floor(rng() * city.regionTowns.length)]} (${city.regionShort})`, isRegion: true };
    } else {
        const availableDistricts = city.districts.filter(d => !cityState.districtsOff.includes(d));
        if (!availableDistricts.length) return null;
        place = { label: availableDistricts[Math.floor(rng() * availableDistricts.length)], isRegion: false };
    }

    const directionId = activeDirectionIds[Math.floor(rng() * activeDirectionIds.length)];
    const cost = priceFor(directionId, city);
    const state2 = store.get();

    state2.leadCounter += 1;
    const lead = {
        id: 'lead-' + state2.leadCounter,
        number: state2.leadCounter,
        createdAt: Date.now(),
        name: FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)],
        phone: generatePhone(rng),
        phoneRevealed: false,
        directionId,
        cityId: city.id,
        place: place.label,
        isRegion: place.isRegion,
        source: rng() < 0.55 ? 'Заявка с сайта' : 'Звонок',
        status: 'new',
        cost,
        note: '',
        example: buildExample(rng, directionId),
        dealAmount: null,
        dispute: null
    };

    return lead;
}

function tick() {
    if (store.canReceiveLeads()) {
        const lead = generateLead();
        if (lead) {
            store.addLead(lead);
            const direction = DIRECTIONS_BY_ID[lead.directionId];
            showToast(`Новая заявка: ${direction ? direction.title : ''} · ${lead.place}`);
            playSound();
            maybeSystemNotification('Новая заявка', `${direction ? direction.title : ''} · ${lead.place}`);
            store.addNotification({
                type: 'lead',
                leadId: lead.id,
                text: `Новая заявка №${lead.number}: ${direction ? direction.title : ''}, ${lead.place}`
            });
        }
    }

    checkAutoEvents();
    scheduleNext();
}

function checkAutoEvents() {
    const state = store.get();

    const belowThreshold = state.balance < state.balanceThreshold;
    if (belowThreshold && !wasBelowThreshold) {
        store.addNotification({ type: 'balance', text: `Баланс ниже ${formatMoney(state.balanceThreshold)}` });
    }
    wasBelowThreshold = belowThreshold;

    const limitReached = state.todayCount >= state.dailyLimit;
    if (limitReached && !wasLimitReached) {
        store.addNotification({ type: 'limit', text: 'Дневной лимит достигнут' });
    }
    wasLimitReached = limitReached;
}

function scheduleNext(initial) {
    clearTimeout(timer);
    const delay = initial
        ? 14000 + Math.random() * 8000
        : 35000 + Math.random() * 45000;
    timer = setTimeout(tick, delay);
}

export function startFlow() {
    scheduleNext(true);
}

export function stopFlow() {
    clearTimeout(timer);
}
